import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Dropdown } from 'components/ui/Dropdown'
import { PageHeader } from 'components/layout/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Modal } from 'components/ui/Modal'
import { CreateRoleModal } from 'features/roles/components/CreateRoleModal'
import { DeleteRoleModal } from 'features/roles/components/DeleteRoleModal'
import { 
  rolesApi, 
  type Role, 
  type Permission, 
  type UserWithRoles
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  Users, 
  UserCheck,
  Eye,
  EyeOff,
  Search,
  Filter
} from 'lucide-react'

export function RoleManagement() {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { user: currentUser } = useAuth()
  
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [users, setUsers] = useState<UserWithRoles[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [deletingRole, setDeletingRole] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<string>('')
  const [filterSystem, setFilterSystem] = useState<string>('')
  
  // Check if current user is an organization account (has organization)
  // Organization can be a number (ID) or an object, but not null/undefined for org accounts
  // System accounts have organization as null or undefined
  // We need to explicitly check for null/undefined to distinguish system accounts
  const userOrg = currentUser?.organization
  const isOrganizationAccount = userOrg !== null && userOrg !== undefined && userOrg !== 0

  useEffect(() => {
    fetchData()
  }, [])
  
  // Debug: Log organization status
  useEffect(() => {
    if (currentUser) {
      console.log('RoleManagement Debug:', {
        userEmail: currentUser.email,
        organization: currentUser.organization,
        isOrganizationAccount,
        organizationType: typeof currentUser.organization
      })
    }
  }, [currentUser, isOrganizationAccount])

  const fetchData = async () => {
    try {
      setLoading(true)
      // Fetch roles and users as before
      const [rolesRes, usersRes] = await Promise.all([
        rolesApi.getRoles(),
        rolesApi.getUsersWithRoles()
      ])

      // Fetch all permissions across pages
      let page = 1
      const allPermissions: Permission[] = []
      while (true) {
        const res = await rolesApi.getPermissions(undefined, page)
        const data: any = res.data as any
        const items: Permission[] = Array.isArray(data) ? data : (data?.results || [])
        allPermissions.push(...items)
        const hasNext = !Array.isArray(data) && !!data?.next
        if (!hasNext || items.length === 0) break
        page += 1
      }
      
      // Handle different response structures
      const rolesData = Array.isArray(rolesRes.data)
        ? rolesRes.data
        : (rolesRes.data as { results?: Role[] })?.results || []
      const usersData = Array.isArray(usersRes.data)
        ? usersRes.data
        : (usersRes.data as { results?: UserWithRoles[] })?.results || []
      
      setRoles(rolesData)
      setPermissions(allPermissions)
      setUsers(usersData)
    } catch (error) {
      console.error('Error fetching data:', error)
      
      // Set empty arrays as fallback
      setRoles([])
      setPermissions([])
      setUsers([])
      
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('roles.messages.loadError'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFormSuccess = () => {
    fetchData()
  }

  const handleEditRole = (role: Role) => {
    setSelectedRole(role)
    setShowEditModal(true)
  }

  const openDeleteModal = (role: Role) => {
    if (role.is_system_role) {
      addToast({
        type: 'error',
        title: t('roles.messages.cannotDelete'),
        message: t('roles.messages.cannotDeleteSystem'),
        duration: 5000
      })
      return
    }
    setSelectedRole(role)
    setShowDeleteModal(true)
  }

  const closePermissionModal = () => {
    setShowPermissionModal(false)
    setSelectedRole(null)
  }

  const handleDeleteRole = async () => {
    if (!selectedRole) return

    try {
      setDeletingRole(true)
      await rolesApi.deleteRole(selectedRole.id)
      setRoles(roles.filter(r => r.id !== selectedRole.id))
      setShowDeleteModal(false)
      setSelectedRole(null)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('roles.messages.roleDeleted'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error deleting role:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('roles.messages.deleteError'),
        duration: 5000
      })
    } finally {
      setDeletingRole(false)
    }
  }

  const getPermissionsByCategory = (category: string) => {
    return (permissions || []).filter(p => p.category === category)
  }

  const getPermissionCategories = () => {
    return [...new Set((permissions || []).map(p => p.category))]
  }

  const getUsersWithRole = (roleId: number) => {
    let filteredUsers = (users || []).filter(user => user.roles.some(role => role.role === roleId))
    
    // For organization accounts, filter out superusers
    if (isOrganizationAccount) {
      filteredUsers = filteredUsers.filter(user => !user.is_superuser)
    }
    
    return filteredUsers
  }

  const filteredRoles = (roles || []).filter(role => {
    const roleNameLower = role.name.toLowerCase().trim()
    const isSuperAdminRole = roleNameLower === 'super admin' || roleNameLower === 'superadmin'
    
    // Only hide Super Admin for organization accounts
    // System accounts (isOrganizationAccount === false) should see Super Admin
    if (isSuperAdminRole && isOrganizationAccount) {
      return false // Hide Super Admin ONLY for organization accounts
    }
    
    // For system accounts, Super Admin will pass through (isOrganizationAccount is false)
    
    const matchesSearch = !searchTerm || role.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesActive = !filterActive || (filterActive === 'active' ? role.is_active : !role.is_active)
    const matchesSystem = !filterSystem || (filterSystem === 'system' ? role.is_system_role : !role.is_system_role)
    return matchesSearch && matchesActive && matchesSystem
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-[1149px]:space-y-4 max-[1149px]:px-4 max-[1149px]:py-4">
      <div className="space-y-2 max-[1149px]:space-y-3">
        <PageHeader title={t('roles.title')} />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-end gap-2 max-[1149px]:gap-3">
          <div className="flex items-center gap-2 self-end md:self-auto max-[1149px]:w-full max-[1149px]:self-stretch">
            <div className="relative w-72 md:w-96 lg:w-[28rem] max-[1149px]:flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
              <Input
                placeholder={`${t('common.search')} ${t('nav.roles').toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 max-[1149px]:pl-9 max-[1149px]:h-9 max-[1149px]:text-sm"
              />
            </div>
            <Dropdown
              trigger={
                <Button variant="outline" className="flex items-center gap-2 max-[1149px]:h-9 max-[1149px]:w-9 max-[1149px]:p-0 flex-shrink-0">
                  <Filter className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span className="max-[1149px]:hidden">{t('common.filters')}</span>
                </Button>
              }
            >
              <div className="p-3 space-y-3 max-[1149px]:p-2 max-[1149px]:space-y-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1 max-[1149px]:text-[10px]">{t('common.status')}</label>
                  <select
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm max-[1149px]:text-xs max-[1149px]:py-1.5"
                  >
                    <option value="">{t('common.all')}</option>
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1 max-[1149px]:text-[10px]">{t('roles.system')}</label>
                  <select
                    value={filterSystem}
                    onChange={(e) => setFilterSystem(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm max-[1149px]:text-xs max-[1149px]:py-1.5"
                  >
                    <option value="">{t('common.all')}</option>
                    <option value="system">{t('roles.systemRoles')}</option>
                    <option value="non_system">{t('roles.nonSystemRoles')}</option>
                  </select>
                </div>
                <div className="flex gap-2 pt-1 max-[1149px]:pt-0">
                  <Button variant="outline" onClick={() => { setFilterActive(''); setFilterSystem('') }} className="flex-1 max-[1149px]:h-9 max-[1149px]:text-sm">
                    {t('common.clear')}
                  </Button>
                  <Button className="flex-1 max-[1149px]:h-9 max-[1149px]:text-sm">
                    {t('common.apply')}
                  </Button>
                </div>
              </div>
            </Dropdown>
            <Button onClick={() => setShowCreateModal(true)} className="max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:flex-1">
              <Plus className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
              <span className="max-[1149px]:text-sm">{t('roles.createRole')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Roles Grid */}
      {filteredRoles && filteredRoles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-[1149px]:grid-cols-1 max-[1149px]:gap-4">
          {filteredRoles.map((role) => (
          <Card key={role.id} className="relative max-[1149px]:border">
            <CardHeader className="pb-3 max-[1149px]:pb-2 max-[1149px]:p-4">
              <div className="flex items-center justify-between max-[1149px]:gap-2">
                <div className="flex items-center space-x-2 max-[1149px]:space-x-1.5 flex-1 min-w-0">
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0 max-[1149px]:w-2.5 max-[1149px]:h-2.5" 
                    style={{ backgroundColor: role.color }}
                  />
                  <CardTitle className="text-lg max-[1149px]:text-base truncate">{role.name}</CardTitle>
                </div>
                <div className="flex space-x-1 max-[1149px]:space-x-0.5 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedRole(role)
                      setShowPermissionModal(true)
                    }}
                    className="max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:p-0"
                  >
                    <Eye className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  </Button>
                  {!role.is_system_role && (
                    <>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleEditRole(role)}
                    className="max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:p-0"
                  >
                    <Edit className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(role)}
                        className="text-red-600 hover:text-red-700 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:p-0"
                      >
                        <Trash2 className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="max-[1149px]:p-4 max-[1149px]:pt-0">
              <p className="text-sm text-muted-foreground mb-4 max-[1149px]:text-xs max-[1149px]:mb-3 line-clamp-2">
                {t(`roles.roleDescriptions.${role.name}`, role.description)}
              </p>
              
              <div className="space-y-3 max-[1149px]:space-y-2">
                <div className="flex items-center justify-between text-sm max-[1149px]:text-xs">
                  <span className="text-muted-foreground">{t('roles.permissions')}</span>
                  <Badge variant="secondary" className="max-[1149px]:text-xs max-[1149px]:px-1.5">{role.permission_count}</Badge>
                </div>
                
                <div className="flex items-center justify-between text-sm max-[1149px]:text-xs">
                  <span className="text-muted-foreground">{t('users.title')}</span>
                  <Badge variant="outline" className="max-[1149px]:text-xs max-[1149px]:px-1.5">
                    {getUsersWithRole(role.id).length}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between text-sm max-[1149px]:text-xs">
                  <span className="text-muted-foreground">{t('common.status')}</span>
                  <Badge variant={role.is_active ? "default" : "secondary"} className="max-[1149px]:text-xs max-[1149px]:px-1.5">
                    {role.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </div>
                
                {role.is_system_role && (
                  <Badge variant="destructive" className="w-full justify-center max-[1149px]:text-xs max-[1149px]:py-1">
                    <Shield className="h-3 w-3 mr-1 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                    {t('roles.systemRole')}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
          ))}
        </div>
      ) : (
        <Card className="max-[1149px]:border">
          <CardContent className="text-center py-8 max-[1149px]:py-6">
            <p className="text-muted-foreground max-[1149px]:text-sm">{t('roles.noRolesFound')}</p>
          </CardContent>
        </Card>
      )}

      {/* Users with Roles removed per request */}

      {/* Create/Edit Role Modal */}
      <CreateRoleModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false)
          setShowEditModal(false)
          setSelectedRole(null)
        }}
        editingRole={selectedRole}
        onSuccess={handleFormSuccess}
      />

      {/* Permission Details Modal */}
      <Modal
        isOpen={showPermissionModal && !!selectedRole}
        onClose={closePermissionModal}
        title={
          selectedRole
            ? `${selectedRole.name} - ${t('roles.permissions')}`
            : t('roles.permissions')
        }
        size="lg"
        className="max-h-[85vh] max-[1149px]:max-h-[95vh]"
      >
        {selectedRole && (
          <div className="space-y-4 max-[1149px]:space-y-3">
            {getPermissionCategories().map((category) => {
              const categoryPermissions = getPermissionsByCategory(category)
              return (
                <div key={category} className="border rounded-lg p-4 max-[1149px]:p-3">
                  <h3 className="font-medium mb-2 max-[1149px]:text-sm max-[1149px]:mb-1.5">
                    {t(`roles.permissionCategories.${category}`)}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-[1149px]:grid-cols-1 max-[1149px]:gap-1.5">
                    {categoryPermissions.map((permission) => {
                      const hasThisPermission = selectedRole.permissions.some(
                        (rp) => rp.id === permission.id
                      )
                      return (
                        <div
                          key={permission.id}
                          className={`flex items-center space-x-2 text-sm p-2 rounded max-[1149px]:text-xs max-[1149px]:p-1.5 max-[1149px]:space-x-1.5 ${
                            hasThisPermission
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}
                        >
                          {hasThisPermission ? (
                            <Eye className="h-4 w-4 text-green-600 flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-gray-400 flex-shrink-0 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          )}
                          <span className="truncate">
                            {t(`roles.permissionNames.${permission.codename}`) ||
                              permission.name}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Modal>

      {/* Delete Role Modal */}
      <DeleteRoleModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedRole(null)
        }}
        onConfirm={handleDeleteRole}
        role={selectedRole}
        loading={deletingRole}
      />
    </div>
  )
}
